import { ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'

import { useInfringementProcessingStore } from '@/features/infringement/store'
import { graphql } from '@/shared/graphql/router'
import { CreateReportErrorCode } from '@/shared/graphql/router/graphql'
import { useUserStore } from '@/shared/stores/user'

import type { FlaggedInfringement } from '../FlaggedMatch/useFlaggedMatch'

const CREATE_REPORT_MUTATION = graphql(`
  mutation CreateReport($input: CreateReportInput!) {
    createReport(input: $input) {
      success
      error {
        code
        message
      }
    }
  }
`)

const GENERIC_ERROR =
  'Something went wrong creating your report. Please try again.'

export const useCreateFlaggedReport = () => {
  const userStore = useUserStore()
  const processingStore = useInfringementProcessingStore()
  const error = ref<string | null>(null)

  const { mutate, loading } = useMutation(CREATE_REPORT_MUTATION, {
    clientId: 'router',
  })

  const create = async (infringement: FlaggedInfringement) => {
    error.value = null
    const copyrightId = infringement.copyrights[0]?.id
    if (!copyrightId) {
      error.value = GENERIC_ERROR
      return false
    }

    try {
      const res = await mutate({
        input: { copyrightId, comparingId: infringement.infringingFile.id },
      })
      const payload = res?.data?.createReport
      if (payload?.success) {
        processingStore.addExistingProcessingReport(infringement)
        userStore.updateUser({ isCreatingReport: true })
        return true
      }
      error.value =
        payload?.error?.code === CreateReportErrorCode.EvidenceNotReady
          ? payload.error.message ||
            'This recording is not ready for reports yet. No report credit was used.'
          : GENERIC_ERROR
    } catch {
      error.value = GENERIC_ERROR
    }
    return false
  }

  return { create, loading, error }
}
